type EnvLike = Record<string, string | boolean | undefined>;

interface MfeAppUrlOptions {
  port?: number;
  env?: EnvLike;
  isDev?: boolean;
  currentOrigin?: string;
}

const DEFAULT_PUBLIC_HOST = 'apps.local.openedx.io';

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function getCurrentOrigin(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return window.location.origin;
}

function getMfeBaseOrigin(
  env: EnvLike,
  isDev: boolean,
  currentOrigin: string | undefined
): string | undefined {
  const configuredBaseUrl = env.VITE_MFE_BASE_URL;

  if (typeof configuredBaseUrl === 'string' && configuredBaseUrl.trim() !== '') {
    return trimTrailingSlash(configuredBaseUrl);
  }

  if (isDev) {
    const host =
      (typeof env.VITE_PUBLIC_HOST === 'string' && env.VITE_PUBLIC_HOST) ||
      (typeof env.VITE_MFE_HOST === 'string' && env.VITE_MFE_HOST) ||
      DEFAULT_PUBLIC_HOST;
    const protocol =
      typeof env.VITE_MFE_PROTOCOL === 'string' && env.VITE_MFE_PROTOCOL
        ? env.VITE_MFE_PROTOCOL
        : 'http';
    return `${protocol}://${host}`;
  }

  return currentOrigin;
}

export function getMfeAppUrl(appName: string, options: MfeAppUrlOptions = {}): string {
  const env = options.env ?? {};
  const isDev = options.isDev ?? false;
  const currentOrigin = options.currentOrigin ?? getCurrentOrigin();
  const baseOrigin = getMfeBaseOrigin(env, isDev, currentOrigin);

  // A shared proxy origin (VITE_MFE_BASE_URL, e.g. Caddy or the prod CDN) means
  // every app lives on one origin under /{app}/ — no per-app ports.
  const hasProxyOrigin =
    typeof env.VITE_MFE_BASE_URL === 'string' && env.VITE_MFE_BASE_URL.trim() !== '';
  // Path-based routing is on when a proxy origin is configured OR the flag is set.
  const pathRoutingFlag =
    env.VITE_USE_PATH_ROUTING === 'true' || env.VITE_USE_PATH_ROUTING === true;
  const usePathRouting = hasProxyOrigin || pathRoutingFlag;

  if (isDev && typeof options.port === 'number') {
    const host =
      (typeof env.VITE_PUBLIC_HOST === 'string' && env.VITE_PUBLIC_HOST) ||
      (typeof env.VITE_MFE_HOST === 'string' && env.VITE_MFE_HOST) ||
      DEFAULT_PUBLIC_HOST;
    const protocol =
      typeof env.VITE_MFE_PROTOCOL === 'string' && env.VITE_MFE_PROTOCOL
        ? env.VITE_MFE_PROTOCOL
        : 'http';

    // Port-based dev, app served at root ('/'): http://host:port
    if (!usePathRouting) {
      return `${protocol}://${host}:${options.port}`;
    }
    // Direct dev servers WITH a path base ('/{app}/') and no shared proxy origin:
    // include both the port and the app path, e.g. http://host:3001/admin/.
    // (When VITE_MFE_BASE_URL is set, that proxy origin wins via baseOrigin below.)
    if (!hasProxyOrigin) {
      return `${protocol}://${host}:${options.port}/${appName}/`;
    }
  }

  if (!baseOrigin) {
    return `/${appName}/`;
  }

  return `${baseOrigin}/${appName}/`;
}

export function getMfeRouteUrl(
  appName: string,
  path: string,
  options: MfeAppUrlOptions = {}
): string {
  const baseUrl = trimTrailingSlash(getMfeAppUrl(appName, options));
  const normalizedPath = path.replace(/^\/+/, '');
  return normalizedPath ? `${baseUrl}/${normalizedPath}` : `${baseUrl}/`;
}

const MAX_NEXT_URL_LENGTH = 500;

function isAuthLoginUrl(url: string): boolean {
  try {
    const pathname = new URL(url).pathname;
    return pathname.startsWith('/authn/login');
  } catch {
    return url.startsWith('/authn/login');
  }
}

export function getAuthnLoginUrl(next?: string, options: MfeAppUrlOptions = {}): string {
  const authnBase = trimTrailingSlash(
    getMfeAppUrl('authn', { ...options, port: options.port ?? 3004 })
  );
  const loginPath = `${authnBase}/login`;

  // Skip next if it points back to the login page (would cause a loop)
  // or if it's too long to safely include in a URL.
  const safeNext =
    !next || next.length > MAX_NEXT_URL_LENGTH || isAuthLoginUrl(next) ? undefined : next;

  if (loginPath.startsWith('http://') || loginPath.startsWith('https://')) {
    const loginUrl = new URL(loginPath);
    if (safeNext) {
      loginUrl.searchParams.set('next', safeNext);
    }
    return loginUrl.toString();
  }

  if (!safeNext) {
    return loginPath;
  }

  return `${loginPath}?next=${encodeURIComponent(safeNext)}`;
}
